import { useMemo } from "react";
import { Card, Empty, Space, Tag, Typography } from "antd";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { useTranslation } from "react-i18next";

const { Text } = Typography;

interface AuditsByAuditorEntry {
  auditor_id: number | null;
  auditor_name: string | null;
  total: number;
  finalized: number;
  pct: number;
}

interface Props {
  data: AuditsByAuditorEntry[];
}

/**
 * Gráfico de barras horizontales — auditorías asignadas por auditor.
 * Muestra por cada auditor la barra total (`asignadas`) y superpuesta
 * las cerradas (finalizadas + canceladas). El % completado va como
 * label a la derecha para que el coordinador vea de un vistazo quién
 * va al día y quién va tarde.
 *
 * Datos: `PlanningGroupDetail.audits_by_auditor` (backend agrega
 * `Q(status_of_audit__in=('finalized','canceled')) | Q(audit_state__name~/finaliz/)`).
 */
export const AuditsByAuditorChart = ({ data }: Props) => {
  const { t } = useTranslation();

  // Recharts pinta bien con {name, total, finalized, pct}. Etiqueta
  // "Sin asignar" para las filas con auditor null y ordena de mayor
  // a menor por total para que el gráfico sea legible.
  const chartData = useMemo(() => {
    return (data ?? [])
      .map((row) => ({
        name:
          row.auditor_name ||
          t("planning.chart_auditor_unassigned", "Sin asignar"),
        total: row.total,
        finalized: row.finalized,
        pct: row.pct,
      }))
      .sort((a, b) => b.total - a.total);
  }, [data, t]);

  // Altura dinámica — 34px por auditor + margen. Cap en 520 para no
  // desbordar cuando hay muchos auditores; recharts scrollea con el
  // padre si hace falta.
  const height = Math.min(520, Math.max(160, chartData.length * 34 + 60));

  return (
    <Card
      size="small"
      title={
        <Space size="small">
          <strong>
            {t(
              "planning.chart_audits_by_auditor_title",
              "Auditorías por auditor",
            )}
          </strong>
          <Tag color="blue">{chartData.length}</Tag>
        </Space>
      }
      extra={
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t(
            "planning.chart_audits_by_auditor_hint",
            "Asignadas vs. cerradas (finalizadas + canceladas)",
          )}
        </Text>
      }
    >
      {chartData.length === 0 ? (
        <Empty
          description={t(
            "planning.chart_audits_by_auditor_empty",
            "Aún no hay auditorías asignadas",
          )}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 8, right: 40, bottom: 8, left: 16 }}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" allowDecimals={false} />
            <YAxis
              dataKey="name"
              type="category"
              width={140}
              tick={{ fontSize: 12 }}
            />
            <Tooltip
              formatter={(value: number, name: string) => {
                if (name === "pct") return [`${value}%`, "% completadas"];
                if (name === "total") return [value, "Asignadas"];
                if (name === "finalized") return [value, "Cerradas"];
                return [value, name];
              }}
            />
            <Legend
              formatter={(value) => {
                if (value === "total") return "Asignadas";
                if (value === "finalized") return "Cerradas";
                return value;
              }}
            />
            <Bar dataKey="total" fill="#d9d9d9" name="total" barSize={14} />
            <Bar dataKey="finalized" fill="#52c41a" name="finalized" barSize={14}>
              <LabelList
                dataKey="pct"
                position="right"
                formatter={((v: unknown) => `${v}%`) as any}
                style={{ fontSize: 11, fill: "#595959" }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
};
